// Asbestos Floorplan Intelligence — building-scoped workspace.
// Phase 1 · R1: floors + raw upload → OpenCV standardisation → cleaned base preview.
// The editable canvas + asbestos zone drawing arrive in R2/R3.
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Plus, Loader2, Upload, ScanLine, Trash2, Pencil,
    FileText, ImageOff, Building as BuildingIcon, Check, X, Image as ImageIcon,
    Link2, Download, MessageSquare, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FloorCanvas from "@/pages/asbestos/FloorCanvas";
import { ShareRegisterDialog } from "@/pages/asbestos/ShareRegisterDialog";
import { AsbestosFeedbackDialog } from "@/pages/asbestos/AsbestosFeedbackDialog";
import AsbestosSurveysPanel from "@/pages/asbestos/AsbestosSurveysPanel";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);

export default function AsbestosWorkspace() {
    const { buildingId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const deepFloorId = searchParams.get("floor");
    const deepZoneId = searchParams.get("zone");
    const autoOpenFeedback = searchParams.get("feedback") === "1";
    const [loading, setLoading] = useState(true);
    const [building, setBuilding] = useState(null);
    const [set, setSet] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [activeFloorId, setActiveFloorId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [renaming, setRenaming] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [askKind, setAskKind] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(autoOpenFeedback);
    const [exporting, setExporting] = useState(false);
    const [surveys, setSurveys] = useState([]);
    const [surveysOpen, setSurveysOpen] = useState(true);
    const [floorsOpen, setFloorsOpen] = useState(true);
    const fileRef = useRef(null);
    const sourceKindRef = useRef("photo");
    const canShare = user && (CAN_EDIT_ROLES.has(user.role) || user.role === "client_admin");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/asbestos/building/${buildingId}`);
            setBuilding(data.building);
            setSet(data.set);
            setCanEdit(data.can_edit);
            const floors = data.set?.floors || [];
            setActiveFloorId((cur) => {
                if (cur && floors.some((f) => f.id === cur)) return cur;
                if (deepFloorId && floors.some((f) => f.id === deepFloorId)) return deepFloorId;
                return floors[0]?.id || null;
            });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load asbestos workspace");
        } finally {
            setLoading(false);
        }
    }, [buildingId, deepFloorId]);

    useEffect(() => { load(); }, [load]);

    // Keep the in-memory set in sync with canvas autosaves so switching floors / screens
    // never shows stale data (fixes "drawn zone lost when flipping screens").
    const onFloorUpdate = useCallback((floorId, patch) => {
        setSet((prev) => prev ? {
            ...prev,
            floors: (prev.floors || []).map((f) => f.id === floorId ? { ...f, ...patch } : f),
        } : prev);
    }, []);

    const loadDocuments = useCallback(() => {
        api.get("/documents", { params: { building_id: buildingId } })
            .then((r) => setDocuments(r.data || [])).catch(() => setDocuments([]));
    }, [buildingId]);
    useEffect(() => { loadDocuments(); }, [loadDocuments, set]);

    const loadSurveys = useCallback(() => {
        api.get(`/asbestos/building/${buildingId}/surveys`)
            .then((r) => setSurveys(r.data || [])).catch(() => setSurveys([]));
    }, [buildingId]);
    useEffect(() => { loadSurveys(); }, [loadSurveys]);

    const floors = (set?.floors || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const activeFloor = floors.find((f) => f.id === activeFloorId) || null;

    async function addFloor() {
        const name = window.prompt("Floor name", `Floor ${floors.length + 1}`);
        if (name === null) return;
        try {
            const { data } = await api.post(`/asbestos/building/${buildingId}/floors`, { name: name || undefined });
            await load();
            setActiveFloorId(data.id);
            toast.success("Floor added");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not add floor");
        }
    }

    async function deleteFloor(f) {
        if (!window.confirm(`Delete "${f.name}" and its standardised plan from this set?`)) return;
        try {
            await api.delete(`/asbestos/floors/${f.id}`);
            toast.success("Floor deleted");
            setActiveFloorId(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not delete floor");
        }
    }

    async function saveRename(f, name) {
        setRenaming(null);
        if (!name || name === f.name) return;
        try {
            await api.patch(`/asbestos/floors/${f.id}`, { name });
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Rename failed");
        }
    }

    async function onPickFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !activeFloor) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("source_kind", sourceKindRef.current || "photo");
        setBusy(true);
        const t = toast.loading("Standardising floorplan… (denoise · enhance · clean lines)");
        try {
            await api.post(`/asbestos/floors/${activeFloor.id}/source`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Standardised — cleaned base plan ready", { id: t });
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not process this floorplan", { id: t });
        } finally {
            setBusy(false);
        }
    }

    function chooseKind(kind) {
        sourceKindRef.current = kind;
        setAskKind(false);
        fileRef.current?.click();
    }

    async function exportPdf() {
        setExporting(true);
        try {
            const res = await api.get(`/asbestos/building/${buildingId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filenameFromResponse(res, `${building?.name || "Asbestos-Register"}.pdf`);
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not export the register — add at least one asbestos record first");
        } finally {
            setExporting(false);
        }
    }

    if (loading) {
        return <div className="grid place-items-center py-24"><Loader2 className="animate-spin" style={{ color: "#C8632A" }} /></div>;
    }

    return (
        <div data-testid="asbestos-workspace" className="space-y-5">
            {/* Header */}
            <div className="flex items-start gap-3">
                <button data-testid="asbestos-back" onClick={() => navigate("/app/asbestos")}
                    className="mt-1 w-8 h-8 rounded-[8px] grid place-items-center hover:bg-[#F0EDE8]" title="Back to Sites & Buildings">
                    <ArrowLeft className="w-4 h-4" style={{ color: "#6F6A64" }} />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold" style={{ color: "#C8632A" }}>
                        <ScanLine className="w-3.5 h-3.5" /> Asbestos Floorplan Intelligence
                    </div>
                    <h1 className="text-2xl font-bold text-[#1F1F1F] flex items-center gap-2">
                        <BuildingIcon className="w-5 h-5" style={{ color: "#9A968F" }} /> {building?.name}
                    </h1>
                    <p className="text-[12.5px]" style={{ color: "#6F6A64" }}>
                        Upload a floorplan (scan, photo or PDF). It is cleaned into a standardised base plan you can map asbestos onto.
                        Standardised outputs are saved as controlled Documents on this building.
                    </p>
                </div>
                <div className="flex items-center gap-2 mt-1">
                    <button data-testid="asbestos-feedback-btn" onClick={() => setFeedbackOpen(true)}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[8px] border text-[12.5px] font-semibold" style={{ borderColor: "#E2DDD5", color: "#1F1F1F" }}>
                        <MessageSquare className="w-3.5 h-3.5" style={{ color: "#C8632A" }} /> Feedback
                    </button>
                    {canShare && (
                        <button data-testid="asbestos-share-btn" onClick={() => setShareOpen(true)}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[8px] border text-[12.5px] font-semibold" style={{ borderColor: "#E2DDD5", color: "#1F1F1F" }}>
                            <Link2 className="w-3.5 h-3.5" style={{ color: "#C8632A" }} /> Share
                        </button>
                    )}
                    <button data-testid="asbestos-export-pdf-btn" disabled={exporting} onClick={exportPdf}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[8px] text-white text-[12.5px] font-semibold disabled:opacity-60" style={{ background: "#C8632A" }}>
                        {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export PDF
                    </button>
                </div>
            </div>

            {shareOpen && (
                <ShareRegisterDialog clientId={building?.client_id} canInviteClientAdmin={user && CAN_EDIT_ROLES.has(user.role)}
                    shareUrl={`${window.location.origin}/app/buildings/${buildingId}/asbestos`} onClose={() => setShareOpen(false)} />
            )}
            {feedbackOpen && (
                <AsbestosFeedbackDialog buildingId={buildingId} canModerate={user && CAN_EDIT_ROLES.has(user.role)}
                    onClose={() => setFeedbackOpen(false)} />
            )}

            <div className="grid grid-cols-12 gap-5">
                {/* Surveys + Floor selector */}
                <div className="col-span-12 lg:col-span-3 space-y-4">
                    {/* Surveys section (collapsible) */}
                    <div>
                        <button type="button" onClick={() => setSurveysOpen((v) => !v)} data-testid="asbestos-surveys-toggle"
                            className="w-full flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold py-0.5" style={{ color: "#9A968F" }}>
                            {surveysOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            Surveys ({surveys.length})
                        </button>
                        {surveysOpen && (
                            <div className="mt-2">
                                <AsbestosSurveysPanel buildingId={buildingId} canEdit={canEdit} surveys={surveys} onRefresh={loadSurveys} />
                            </div>
                        )}
                    </div>

                    {/* Floors section (collapsible) */}
                    <div>
                        <div className="flex items-center justify-between">
                            <button type="button" onClick={() => setFloorsOpen((v) => !v)} data-testid="asbestos-floors-toggle"
                                className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold py-0.5" style={{ color: "#9A968F" }}>
                                {floorsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                Floors ({floors.length})
                            </button>
                            {canEdit && (
                                <button data-testid="asbestos-add-floor" onClick={addFloor}
                                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-[6px] text-white" style={{ background: "#1F1F1F" }}>
                                    <Plus className="w-3 h-3" /> Floor
                                </button>
                            )}
                        </div>
                        {floorsOpen && (
                            <div className="space-y-2 mt-2">
                                {floors.length === 0 ? (
                                    <div className="text-[12px] italic rounded-[10px] border border-dashed p-4" style={{ color: "#9A968F", borderColor: "#E2DDD5" }}>
                                        No floors yet.{canEdit ? " Add a floor to begin." : ""}
                                    </div>
                                ) : floors.map((f) => (
                                    <div key={f.id} data-testid={`asbestos-floor-${f.id}`}
                                        onClick={() => setActiveFloorId(f.id)}
                                        className={`group rounded-[10px] border px-3 py-2.5 cursor-pointer transition-colors ${activeFloorId === f.id ? "bg-[#FBEDE6] border-[#E7B79C]" : "bg-white border-[#EFEBE4] hover:bg-[#FCFBF9]"}`}>
                                        <div className="flex items-center gap-2">
                                            <ScanLine className="w-3.5 h-3.5 shrink-0" style={{ color: f.standardised_path ? "#C8632A" : "#C7C2BA" }} />
                                            {renaming === f.id ? (
                                                <input autoFocus defaultValue={f.name}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onBlur={(e) => saveRename(f, e.target.value.trim())}
                                                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setRenaming(null); }}
                                                    data-testid={`asbestos-floor-rename-${f.id}`}
                                                    className="flex-1 min-w-0 text-[13px] font-semibold bg-white border rounded px-1.5 py-0.5" style={{ borderColor: "#E7B79C" }} />
                                            ) : (
                                                <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-[#1F1F1F]">{f.name}</span>
                                            )}
                                            {canEdit && renaming !== f.id && (
                                                <span className="hidden group-hover:inline-flex gap-1">
                                                    <button onClick={(e) => { e.stopPropagation(); setRenaming(f.id); }} title="Rename"><Pencil className="w-3 h-3" style={{ color: "#9A968F" }} /></button>
                                                    <button onClick={(e) => { e.stopPropagation(); deleteFloor(f); }} title="Delete"><Trash2 className="w-3 h-3" style={{ color: "#B33A2E" }} /></button>
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[10.5px] mt-0.5 pl-5.5" style={{ color: "#9A968F" }}>
                                            {f.standardised_path ? `${f.width}×${f.height}px · standardised` : "Awaiting upload"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Active floor canvas / preview */}
                <div className="col-span-12 lg:col-span-9">
                    {!activeFloor ? (
                        <div className="rounded-[12px] border border-dashed grid place-items-center py-24 text-center" style={{ borderColor: "#E2DDD5" }}>
                            <div>
                                <ScanLine className="w-8 h-8 mx-auto mb-2" style={{ color: "#D8D2C8" }} />
                                <div className="text-[13px]" style={{ color: "#9A968F" }}>Select or add a floor to start.</div>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-[12px] border bg-white" style={{ borderColor: "#EFEBE4" }}>
                            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "#F0EDE8" }}>
                                <div className="font-semibold text-[#1F1F1F] flex items-center gap-2">{activeFloor.name}</div>
                                {canEdit && (
                                    <div className="flex items-center gap-2">
                                        <input ref={fileRef} type="file" accept="application/pdf,image/*" hidden onChange={onPickFile} data-testid="asbestos-file-input" />
                                        <button data-testid="asbestos-upload-btn" disabled={busy}
                                            onClick={() => setAskKind(true)}
                                            className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-white disabled:opacity-50" style={{ background: "#C8632A" }}>
                                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                            {activeFloor.standardised_path ? "Replace & re-standardise" : "Upload floorplan"}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="p-4">
                                {activeFloor.standardised_path ? (
                                    <>
                                        <FloorCanvas key={activeFloor.id} floor={activeFloor} canEdit={canEdit} documents={documents} onFloorUpdate={onFloorUpdate}
                                            buildingId={building?.id} clientId={building?.client_id} onDocsRefresh={loadDocuments}
                                            initialZoneId={activeFloor.id === deepFloorId ? deepZoneId : null} />
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11.5px]" style={{ color: "#6F6A64" }}>
                                            <span className="inline-flex items-center gap-1"><ScanLine className="w-3.5 h-3.5" style={{ color: "#C8632A" }} /> Standardised base · {activeFloor.width}×{activeFloor.height}px</span>
                                            {activeFloor.standardised_document_id && (
                                                <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" style={{ color: "#2A5DB0" }} /> Saved as a controlled Document (As-built drawings)</span>
                                            )}
                                            {activeFloor.source_filename && <span className="truncate">Source: {activeFloor.source_filename}</span>}
                                        </div>
                                    </>
                                ) : (
                                    <button data-testid="asbestos-empty-upload" disabled={busy || !canEdit}
                                        onClick={() => canEdit && setAskKind(true)}
                                        className="w-full rounded-[12px] border-2 border-dashed grid place-items-center py-20 text-center disabled:opacity-60" style={{ borderColor: "#E2DDD5" }}>
                                        <div>
                                            {busy ? <Loader2 className="w-7 h-7 mx-auto mb-2 animate-spin" style={{ color: "#C8632A" }} />
                                                : <ImageOff className="w-7 h-7 mx-auto mb-2" style={{ color: "#D8D2C8" }} />}
                                            <div className="text-[13px] font-medium text-[#1F1F1F]">
                                                {canEdit ? "Upload a floorplan (PDF / JPG / PNG)" : "No standardised plan yet"}
                                            </div>
                                            <div className="text-[11.5px] mt-0.5" style={{ color: "#9A968F" }}>
                                                {canEdit ? "Scans and photocopies welcome — we'll denoise and clean it." : "An internal consultant will prepare this plan."}
                                            </div>
                                        </div>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {askKind && (
                <div className="fixed inset-0 z-[80] grid place-items-center p-4" style={{ background: "rgba(20,18,16,0.45)" }}
                    data-testid="asbestos-kind-dialog" onClick={() => setAskKind(false)}>
                    <div className="w-full max-w-md rounded-[14px] bg-white p-5 shadow-2xl" style={{ border: "1px solid #E2DDD5" }} onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-[15px] font-bold text-[#1F1F1F]">What are you uploading?</h3>
                        <p className="text-[12px] mt-1" style={{ color: "#6F6A64" }}>This helps us process it correctly. A CAD/vector PDF gives the most accurate result (real dimensions, cleaner output).</p>
                        <div className="grid gap-2.5 mt-4">
                            <button data-testid="asbestos-kind-photo" onClick={() => chooseKind("photo")}
                                className="text-left rounded-[10px] border p-3 hover:bg-[#FCFBF9]" style={{ borderColor: "#E2DDD5" }}>
                                <div className="font-semibold text-[13px] text-[#1F1F1F] flex items-center gap-2"><ImageIcon className="w-4 h-4" style={{ color: "#C8632A" }} /> Quick photo or scan</div>
                                <div className="text-[11.5px] mt-0.5" style={{ color: "#9A968F" }}>Phone photo, scanned page or image PDF. We clean &amp; standardise it; dimensions via calibration / AI-read.</div>
                            </button>
                            <button data-testid="asbestos-kind-vector" onClick={() => chooseKind("vector")}
                                className="text-left rounded-[10px] border p-3 hover:bg-[#FCFBF9]" style={{ borderColor: "#E2DDD5" }}>
                                <div className="font-semibold text-[13px] text-[#1F1F1F] flex items-center gap-2"><FileText className="w-4 h-4" style={{ color: "#2A5DB0" }} /> CAD / vector PDF <span className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ background: "#2E8B3D" }}>best accuracy</span></div>
                                <div className="text-[11.5px] mt-0.5" style={{ color: "#9A968F" }}>Exported from CAD with selectable text. Enables real geometry + dimensions to be read in directly.</div>
                            </button>
                        </div>
                        <button onClick={() => setAskKind(false)} className="mt-3 text-[12px]" style={{ color: "#9A968F" }}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}
